
import { useState, useRef, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Check, RotateCcw } from "lucide-react";

interface CameraCaptureProps {
  onCapture: (imageDataUrl: string) => void;
  onCancel: () => void;
}

export function CameraCapture({ onCapture, onCancel }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [isStreamReady, setIsStreamReady] = useState(false);
  const [capturedImageData, setCapturedImageData] = useState<string | null>(null);

  useEffect(() => {
    // Initialize camera when component mounts
    let isMounted = true;
    
    const initCamera = async () => {
      try {
        console.log("CameraCapture: Initializing camera stream");
        const stream = await navigator.mediaDevices.getUserMedia({ 
          video: { 
            facingMode: "environment",
            width: { ideal: 1280 },
            height: { ideal: 720 }
          } 
        });
        
        if (!isMounted) {
          // Clean up if component unmounted during async operation
          stream.getTracks().forEach(track => track.stop());
          return;
        }
        
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          streamRef.current = stream;
          
          // Wait for metadata to load before considering stream ready
          videoRef.current.onloadedmetadata = () => {
            if (isMounted) {
              console.log("CameraCapture: Stream metadata loaded, camera ready");
              setIsStreamReady(true);
            }
          };
          
          // Force play in some browsers that need it
          videoRef.current.play()
            .then(() => {
              if (isMounted) console.log("Video playback started successfully");
            })
            .catch(err => {
              console.error("Error starting video playback:", err);
            });
        }
      } catch (err) {
        console.error("CameraCapture: Error accessing camera:", err);
        if (isMounted) onCancel(); // Go back if camera fails
      }
    };
    
    initCamera();
    
    // Cleanup function
    return () => {
      isMounted = false;
      if (streamRef.current) {
        console.log("CameraCapture: Cleaning up camera stream on unmount");
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    };
  }, [onCancel]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  }, []);

  const captureImage = useCallback(() => {
    if (videoRef.current && isStreamReady) {
      console.log("Capturing image from video stream");
      const canvas = document.createElement("canvas");
      const videoWidth = videoRef.current.videoWidth;
      const videoHeight = videoRef.current.videoHeight;
      
      console.log(`Video dimensions: ${videoWidth}x${videoHeight}`);
      
      if (videoWidth === 0 || videoHeight === 0) {
        console.error("Video dimensions are 0, cannot capture image");
        return;
      }
      
      canvas.width = videoWidth;
      canvas.height = videoHeight;
      const ctx = canvas.getContext("2d");
      
      if (ctx) {
        ctx.drawImage(videoRef.current, 0, 0, canvas.width, canvas.height);
        const imageDataUrl = canvas.toDataURL("image/jpeg", 0.8); // Compression quality 0.8
        setCapturedImageData(imageDataUrl);
        console.log("Image successfully captured");
      }
    } else {
      console.error("Cannot capture: video reference not available or stream not ready");
    }
  }, [isStreamReady]);

  const confirmCapture = useCallback(() => {
    if (capturedImageData) {
      onCapture(capturedImageData);
      stopCamera();
    }
  }, [capturedImageData, onCapture, stopCamera]);

  const retakePhoto = useCallback(() => {
    setCapturedImageData(null);
  }, []);

  return (
    <>
      {!capturedImageData ? (
        // Camera view when capturing
        <>
          <video 
            ref={videoRef} 
            autoPlay 
            playsInline
            muted
            className="w-full h-full object-cover animate-fade-in"
          />
          
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-2 right-2 bg-black/30 text-white hover:bg-black/50"
            onClick={() => {
              stopCamera();
              onCancel();
            }}
          >
            <X size={20} />
          </Button>
          
          <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-3">
            <Button 
              variant="default" 
              onClick={captureImage}
              disabled={!isStreamReady}
              className="px-6 rounded-full shadow-md hover:shadow-lg transition-all"
            >
              <span className="sr-only">Capture</span>
              <div className="w-12 h-12 rounded-full border-4 border-white flex items-center justify-center">
                <div className="w-10 h-10 rounded-full bg-white"></div>
              </div>
            </Button>
          </div>
        </>
      ) : (
        // Preview with controls after capturing
        <>
          <div className="relative w-full h-full">
            <img 
              src={capturedImageData} 
              alt="Captured" 
              className="w-full h-full object-cover" 
            />
            
            <div className="absolute bottom-4 left-0 right-0 flex justify-center space-x-6">
              <Button
                variant="outline"
                size="icon"
                className="w-12 h-12 rounded-full bg-black/30 text-white border-white hover:bg-black/50"
                onClick={retakePhoto}
              >
                <RotateCcw size={20} />
              </Button>
              
              <Button
                variant="outline"
                size="icon"
                className="w-12 h-12 rounded-full bg-black/30 text-white border-white hover:bg-black/50"
                onClick={confirmCapture}
              >
                <Check size={20} />
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

export default CameraCapture;
